import { Component, Input, ViewChild } from '@angular/core';
import { OtpService } from '@app/services/authenticators/otp.service';
import { FieldType, FieldTypeConfig, FormlyConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Button } from '@infocert/ng-ui-kit/models';
import { LocalStorageService } from 'ngx-webstorage';

@Component({
  selector: 'formly-field-ic1-input',
  template: `
    <div class="ic1-form-group">
      <div class="otpInputWrapper">
        <div class="ic1-flex">
          <ng-ui-kit-otp
            #ngUiKitOtp
            [icId]="'formly-otp-' + field.id"
            [type]="'number'"
            [title]="props.label"
            [length]="otpLength"
            [resendBtn]="resendBtnConfig"
            [disclaimerText]="otpUiError ? '' : disclaimerText"
            [error]="otpUiError"
            (onChange)="onChange($event)"
            [timer]="waitingSeconds"
            (timerEnd)="onTimerEnd()">
          </ng-ui-kit-otp>
        </div>
        <div
          *ngIf="otpUiError"
          class="otp-error-message error error-icon otp-formly-error-msg">
          <formly-validation-message [field]="field"></formly-validation-message>
        </div>
        <div
          *ngIf="otpUiError && disclaimerText"
          class="otp-message ic1-input-disclaimer"
          [innerHTML]="disclaimerText"></div>
      </div>
    </div>
  `,
  styleUrls: ['../otp-input/otp-input.component.scss'],
  standalone: false,
})
export class InputOtpType extends FieldType<FieldTypeConfig> {
  constructor(
    private config: FormlyConfig,
    private otpService: OtpService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService
  ) {
    super();
  }

  @Input() submitOnEnter = true;

  @ViewChild('ngUiKitOtp') ngUiKitOtpRef: any;

  value = '';

  touched = false;

  disabled = false;

  waitingSeconds = 40;

  get hardware(): boolean {
    return this.props['hardware'];
  }

  get otpLength(): number {
    return this.hardware ? 6 : 8;
  }

  /** Allineato ad app-otp-input: bordo errore sul widget + testo sotto + disclaimer SMS sotto ancora. */
  get otpUiError(): boolean {
    return this.showError && !!this.formControl?.invalid;
  }

  get resendBtnConfig(): Button | undefined {
    if (this.hardware) {
      return undefined;
    }
    return {
      label: this.translateService.instant('COMMON.ACCORDION_OTP.OTP_SEND_AGAIN'),
      onClick: () => this.sendOtp()
    };
  }

  ngOnInit(): void {
    this.otpService.getEventSubject().subscribe((param: any) => {
      if (param !== undefined) {
        this.resetVal();
      }
    });
  }

  resendDisabled = false;

  onChange = (value: string) => {
    console.log('Formly OTP value changed:', value, 'Expected length:', this.otpLength);
    
    // Solo quando l'OTP è completo impostiamo il valore
    // Altrimenti manteniamo il campo vuoto per forzare la validazione required
    const isComplete = value && value.length === this.otpLength;
    this.formControl.setValue(isComplete ? value : '');
  };

  onTouched = () => {};

  setVal(event: any) {
    this.onChange(event.target.value);
    this.value = event.target.value;
  }

  onEnter(event: KeyboardEvent) {
    console.log('🚀 ~ PasswordInputComponent ~ onEnter ~ event', event);
    if (!this.submitOnEnter) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  resetVal() {
    if (this.ngUiKitOtpRef) {
      this.ngUiKitOtpRef.writeValue('');
    }
  }

  writeValue(val: string) {
    this.value = val;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  sendOtp() {
    if (this.resendDisabled) {
      return;
    }

    this.resendDisabled = true;
    this.otpService.generateCode().subscribe({
      next: () => {},
      error: error => {
        console.error(error);
        this.resendDisabled = false;
      },
      complete: () => {},
    });
  }

  onTimerEnd() {
    this.resendDisabled = false;
  }

  get disclaimerText(): string {
    if (this.hardware)
      return this.translateService.instant(
        'COMMON.ACCORDION_OTP.HARDWARE_MESSAGE'
      );
    const cert = this.localStorageService.retrieve('certificate');
    const phoneNumber = cert?.telephone || '';
    const maskedNumber = this.maskTelephone(phoneNumber);
    if (maskedNumber) {
      return this.translateService.instant(
        'COMMON.ACCORDION_OTP.OTP_MESSAGE',
        { number: maskedNumber }
      );
    }
    return this.translateService.instant('COMMON.ACCORDION_OTP.OTP_MESSAGE');
  }

  private maskTelephone(value: string): string {
    if (!value) {
      return '';
    }
    const digits = value.replace(/\s+/g, '');
    if (digits.length <= 6) {
      return digits;
    }
    const prefix = digits.slice(0, 3);
    const suffix = digits.slice(-3);
    return `${prefix} ******${suffix}`;
  }
}
